import { Icon, lightTheme } from '@arcadiapower/shrike';
import React, { useState, useLayoutEffect } from 'react';

import { Button } from '~/components/button';
import { ImageSelector, ImageType, HALFMOON_SOLAR, EXPLAINER } from '~/components/image';
import { Separator } from '~/components/separator';
import { Text, StyledUl, UlListItem, UlListWrapper } from '~/components/typography';
import { COMMUNITY_SOLAR_FUNNEL_ID } from '~/funnels/community-solar/config';
import { GO_SOLAR_FUNNEL_ID } from '~/funnels/go-solar/config';
import { SOLAR_LANDSCAPE_FUNNEL_ID } from '~/funnels/solar-landscape/config';
import { solarLandscapeSavingsRate } from '~/funnels/solar-landscape/copy';
import { useCopy } from '~/hooks';
import { User, Utility } from '~/typings/context';
import { UTILITY_IDS } from '~/utils/constants';
import { AccountNumberContext } from '../account-number';
import { BillUploadContext } from '../bill-upload';
import { StyledDialogModal, IconWrapper } from './dual-billing.style';

export type Props = {
  hide: () => void;
  overrideCopy?: (context: DualBillingExplainerContext) => Partial<AccountNumberCopy>;
  context?: BillUploadContext | AccountNumberContext;
};

export const DualBillingModal: React.FC<DualBillingExplainerProps> = ({
  context,
  hide,
  overrideCopy,
  isBillUploadDialog,
}: DualBillingExplainerProps) => {
  const [showMobile, setShowMobile] = useState(false);

  useLayoutEffect(() => {
    const mql = window.matchMedia(`(max-width: ${lightTheme.breakpoints.tabletPortrait}px)`);
    setShowMobile(mql.matches);

    const onChange = (event: MediaQueryListEvent) => setShowMobile(event.matches);

    mql.addEventListener('change', onChange);

    return () => mql.removeEventListener('change', onChange);
  }, []);

  if (showMobile) {
    return (
      <StyledDialogModal aria-label="success-modal" size="full-screen" onDismiss={hide}>
        <DualBillingExplainer
          context={context}
          hide={hide}
          overrideCopy={overrideCopy}
          isBillUploadDialog={isBillUploadDialog}
        />
      </StyledDialogModal>
    );
  }

  return (
    <StyledDialogModal aria-label="success-modal" size="medium" onDismiss={hide}>
      <DualBillingExplainer
        context={context}
        hide={hide}
        overrideCopy={overrideCopy}
        isBillUploadDialog={isBillUploadDialog}
      />
    </StyledDialogModal>
  );
};

export const DualBillingExplainer = ({
  context,
  isBillUploadDialog,
  hide,
  overrideCopy,
}: DualBillingExplainerProps) => {
  const copy = useCopy(context, defaultCopy, overrideCopy);
  const isDominionVA = context?.utility.id === UTILITY_IDS['Dominion Energy VA'];
  const isUnitilMA = context?.utility.id === UTILITY_IDS['Unitil MA'];
  const isSolarLandscape = context?.funnelId === SOLAR_LANDSCAPE_FUNNEL_ID;
  const isMD = context?.utility.state.abbreviation === 'MD';
  const isGoSolar = context?.funnelId === GO_SOLAR_FUNNEL_ID;
  const isCommunitySolar = context?.funnelId === COMMUNITY_SOLAR_FUNNEL_ID;
  const showLMISavingsMessage = () => {
    return isSolarLandscape || (isMD && (isGoSolar || isCommunitySolar));
  };
  const lmiSavingsRate = isSolarLandscape
    ? solarLandscapeSavingsRate(context?.promoCode, context?.utility.state.abbreviation)
    : '25%';

  return (
    <>
      <IconWrapper>
        <ImageSelector type={EXPLAINER} />
      </IconWrapper>
      <Text
        mb={3}
        textStyle={isDominionVA || isBillUploadDialog || isUnitilMA ? 'heading600' : undefined}
      >
        {copy.descriptionFirstLine}
      </Text>
      <Text mb={isDominionVA || isBillUploadDialog ? 6 : undefined}>
        {copy.descriptionSecondLine}
      </Text>
      {!isDominionVA && !isBillUploadDialog && (
        <>
          <StyledUl>
            <UlListItem
              style={{
                color: '#577671',
                gap: '16px',
                textAlign: 'left',
                marginTop: '24px',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'flex-start',
              }}
            >
              <Icon scale={2} icon="Node" color="accent9" style={{ marginTop: '4px' }} />
              <UlListWrapper>
                <Text textStyle="heading500" marginBottom="8px">
                  {copy.itemOneHeading}
                </Text>
                <Text opacity="low">{copy.itemOne}</Text>
              </UlListWrapper>
            </UlListItem>
            <UlListItem
              style={{
                color: '#577671',
                gap: '16px',
                textAlign: 'left',
                marginTop: '16px',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'flex-start',
              }}
            >
              <Icon scale={2} icon="Node" color="accent9" style={{ marginTop: '4px' }} />
              <UlListWrapper>
                <Text textStyle="heading500" marginBottom="8px">
                  {copy.itemTwoHeading}
                </Text>
                <Text opacity="low">{copy.itemTwo}</Text>
              </UlListWrapper>
            </UlListItem>
          </StyledUl>
          {showLMISavingsMessage() ? (
            <Text opacity="low" fontSize="12px" paddingTop="8px">
              *Low-to-moderate income households save {lmiSavingsRate}; all others save 5%.
            </Text>
          ) : null}
          <Separator padding={showLMISavingsMessage() ? '0px' : '24px 0px'} />
          <Text textAlign="center" marginBottom="24px">
            {copy.disclaimer}
          </Text>
        </>
      )}
      {hide && (
        <Button
          type="button"
          onClick={hide}
          mode="primary"
          testId="dual-billing-explainer"
          marginBottom="32px"
        >
          {copy.next}
        </Button>
      )}
    </>
  );
};

export type AccountNumberCopy = ReturnType<typeof defaultCopy>;

export const defaultCopy = (context: Props['context']) => {
  const isVersantBHD = context?.utility.id === UTILITY_IDS['Versant - Bangor Hydro District'];
  const isCMP = context?.utility.id === UTILITY_IDS['Central Maine Power'];
  const isSolarLandscape = context?.funnelId === SOLAR_LANDSCAPE_FUNNEL_ID;
  const isMD = context?.utility.state.abbreviation === 'MD';
  const isUnitilMA = context?.utility.id === UTILITY_IDS['Unitil MA'];

  let savingsRate = '';

  if (isMD) {
    savingsRate = '25%*';
  } else if (isVersantBHD || isCMP) {
    savingsRate = 'up to 15%';
  } else if (isUnitilMA) {
    savingsRate = '10%';
  } else {
    savingsRate = '5-10%';
  }

  return {
    descriptionFirstLine: isUnitilMA
      ? 'How does billing work?'
      : 'When we don’t integrate with your utility account automatically, billing works a little differently.',
    descriptionSecondLine: isUnitilMA
      ? 'Each month, you’ll receive two partial bills:'
      : 'When you enter your utility login credentials, you receive just one consolidated bill from Arcadia. When you enter your utility information manually, you receive two partial bills per month:',
    disclaimer:
      'Because of your solar discount, these two bills will add up to less than you would have paid without solar.',
    itemOne:
      'You’ll receive and pay this like normal. It will just be smaller because it won’t include the portion going to the solar farm.',
    itemTwo: isSolarLandscape
      ? `We'll charge you for the portion of your utility bill coming from solar energy, minus your ${solarLandscapeSavingsRate(
          context.promoCode,
          context.utility.state.abbreviation,
        )}* savings.`
      : `We’ll send you a statement for your solar farm energy usage, minus the savings you receive for using clean energy. You'll see your solar savings of ${savingsRate} here.`,
    itemOneHeading: 'Your utility bill',
    itemTwoHeading: 'Your Arcadia statement',
    imageType: HALFMOON_SOLAR as ImageType,
    next: 'Enter utility account information',
  };
};

export interface DualBillingExplainerProps {
  context?: DualBillingExplainerContext;
  hide: () => void;
  overrideCopy?: (context: DualBillingExplainerContext) => Partial<AccountNumberCopy>;
  isBillUploadDialog?: boolean;
}

export interface DualBillingExplainerContext {
  utility: Utility;
  user: User;
  funnelId?: string;
  promoCode?: string;
}
